import React from "react";
import {
  MdAdd,
  MdDelete,
  MdEdit,
  MdCampaign,
  MdCheck,
  MdPushPin,
  MdOutlinePushPin,
  MdVisibility,
  MdVisibilityOff,
} from "react-icons/md";
import { FiEdit2, FiExternalLink } from "react-icons/fi";
import { useAppLanguage } from "hooks/useAppLanguage";
import SlideOver from "components/ui/SlideOver";
import Modal from "components/ui/Modal";
import ConfirmDialog from "components/ui/ConfirmDialog";
import AttachmentTypeIcon from "components/ui/AttachmentTypeIcon";
import { resolveAttachmentFileKind } from "components/ui/attachmentFileKind";
import { splitFilename } from "lib/filenameParts";
import { formatLocaleDateTime } from "lib/localeFormat";
import { Skeleton, SkeletonLines } from "components/ui/Skeleton";
import EmptyState from "components/ui/EmptyState";
import FileUploader, { type UploadedFile } from "components/ui/FileUploader";
import { useCourse } from "../../context/CourseContext";
import {
  instructorCourseAnnouncementsService,
  type CourseAnnouncement,
  type AnnouncementUpsertBody,
  type AnnouncementAttachment,
} from "services/instructorCourseAnnouncementsService";

function SavedAttachmentThumb({
  att,
  size = "md",
}: {
  att: AnnouncementAttachment;
  size?: "sm" | "md" | "lg" | "compact";
}) {
  const [broken, setBroken] = React.useState(false);
  const ct = att.content_type || "";
  const url = att.url ?? undefined;
  const dim =
    size === "sm"
      ? "h-10 w-10"
      : size === "compact"
        ? "h-12 w-12"
        : size === "lg"
          ? "h-20 w-20 sm:h-[5.25rem] sm:w-[5.25rem]"
          : "h-14 w-14";
  const radius = size === "lg" ? "rounded-2xl" : size === "compact" ? "rounded-lg" : "rounded-xl";
  const keyName = attachmentNameFallback(att);
  const label = att.name ?? keyName;
  const kind = resolveAttachmentFileKind(ct, label);
  const showVideoThumb = !broken && !!url && kind === "video";
  const showImageThumb = !broken && !!url && kind === "image";

  if (showImageThumb) {
    return (
      <div className={`shrink-0 ${dim}`}>
        <img
          src={url}
          alt=""
          className={`${dim} ${radius} object-cover shadow-sm ring-1 ring-black/[0.06] dark:ring-white/10`}
          onError={() => setBroken(true)}
        />
      </div>
    );
  }

  if (showVideoThumb) {
    return (
      <div className={`shrink-0 ${dim}`}>
        <video
          src={url}
          muted
          playsInline
          preload="metadata"
          className={`${dim} ${radius} object-cover shadow-sm ring-1 ring-black/[0.06] dark:ring-white/10`}
          onError={() => setBroken(true)}
        />
      </div>
    );
  }

  return (
    <AttachmentTypeIcon
      contentType={ct}
      fileName={label}
      size={size === "sm" ? "sm" : size === "compact" ? "compact" : size === "lg" ? "lg" : "md"}
    />
  );
}

function attachmentNameFallback(att: AnnouncementAttachment): string {
  if (att.key.includes("/")) return att.key.slice(att.key.lastIndexOf("/") + 1);
  return att.key;
}

function formatAttachmentSize(bytes: number | null | undefined): string | null {
  if (bytes == null || bytes <= 0) return null;
  const units = ["B", "KB", "MB", "GB"];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  const rounded = i === 0 || n >= 10 ? Math.round(n) : Math.round(n * 10) / 10;
  return `${rounded} ${units[i]}`;
}

const VideoJsPlayerLazy = React.lazy(() => import("components/ui/PlyrVideoPlayer"));

function AttachmentPreviewBody({ att, isArabic }: { att: AnnouncementAttachment; isArabic: boolean }) {
  const ct = att.content_type || "";
  const url = att.url ?? undefined;
  const keyName = attachmentNameFallback(att);
  const label = att.name ?? keyName;
  const videoMime = React.useMemo(() => {
    if (ct.startsWith("video/")) return ct;
    const n = label.toLowerCase();
    if (n.endsWith(".mov") || n.endsWith(".qt")) return "video/quicktime";
    if (n.endsWith(".webm")) return "video/webm";
    return "video/mp4";
  }, [ct, label]);

  const isVideoFile =
    ct.startsWith("video/") || /\.(mov|mp4|m4v|webm|mkv|avi|wmv)$/i.test(label);

  const shell = (inner: React.ReactNode) => (
    <div className="rounded-xl bg-gray-50 p-3 dark:bg-white/[0.04]">{inner}</div>
  );

  if (!url) {
    return shell(
      <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
        {isArabic ? "لا يوجد رابط للمعاينة." : "No URL available for preview."}
      </p>
    );
  }

  if (ct.startsWith("image/")) {
    return shell(
      <img
        src={url}
        alt=""
        className="mx-auto max-h-[min(80vh,720px)] w-auto max-w-full rounded-lg object-contain"
      />
    );
  }

  if (isVideoFile) {
    return shell(
      <React.Suspense
        fallback={
          <div className="flex aspect-video w-full items-center justify-center rounded-lg bg-gray-200 dark:bg-navy-900">
            <span className="text-sm text-gray-500 dark:text-gray-400">…</span>
          </div>
        }
      >
        <VideoJsPlayerLazy
          src={url}
          mimeType={videoMime}
          className="max-h-[min(80vh,720px)]"
          brandName={isArabic ? "Easyfollow" : "Easyfollow"}
          brandInitial="E"
        />
      </React.Suspense>
    );
  }

  if (ct.startsWith("audio/")) {
    return shell(
      <div className="flex justify-center py-6">
        <audio src={url} controls className="w-full max-w-md rounded-lg" />
      </div>
    );
  }

  if (ct === "application/pdf" || att.name?.toLowerCase().endsWith(".pdf")) {
    return shell(
      <iframe
        title={att.name ?? "PDF"}
        src={url}
        className="h-[min(75vh,640px)] w-full rounded-lg border border-gray-200 bg-white dark:border-white/10 dark:bg-navy-900"
      />
    );
  }

  return shell(
    <div className="flex flex-col items-center gap-4 py-8 text-center">
      <AttachmentTypeIcon contentType={ct} fileName={label} size="lg" />
      <p className="text-sm text-gray-600 dark:text-gray-300">
        {isArabic ? "معاينة هذا النوع غير متاحة هنا." : "Preview is not available for this file type."}
      </p>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="text-sm font-semibold text-brand-600 hover:underline dark:text-brand-400"
      >
        {isArabic ? "فتح أو تنزيل الملف" : "Open or download file"}
      </a>
    </div>
  );
}

export default function AnnouncementsPage() {
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const { course, setCounts } = useCourse();

  const [items, setItems] = React.useState<CourseAnnouncement[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<CourseAnnouncement | null>(null);
  const [deleteTarget, setDeleteTarget] = React.useState<CourseAnnouncement | null>(null);
  const [form, setForm] = React.useState<AnnouncementUpsertBody>({
    title: "",
    content: "",
    is_pinned: false,
    is_published: true,
  });
  const [saving, setSaving] = React.useState(false);
  const [togglingId, setTogglingId] = React.useState<number | null>(null);
  const [uploaded, setUploaded] = React.useState<UploadedFile[]>([]);
  const [inlineRename, setInlineRename] = React.useState<{
    announcementId: number;
    key: string;
    base: string;
  } | null>(null);
  const [renameSaving, setRenameSaving] = React.useState(false);
  const [previewAtt, setPreviewAtt] = React.useState<AnnouncementAttachment | null>(null);

  const reload = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await instructorCourseAnnouncementsService.list(course.id, { limit: 200 });
      setItems(res.data || []);
      setCounts({ announcements: res.meta?.total ?? res.data.length });
    } finally {
      setLoading(false);
    }
  }, [course.id, setCounts]);

  React.useEffect(() => {
    reload();
  }, [reload]);

  const openCreate = () => {
    setEditing(null);
    setForm({ title: "", content: "", is_pinned: false, is_published: true });
    setUploaded([]);
    setEditorOpen(true);
  };

  const openEdit = (a: CourseAnnouncement) => {
    setEditing(a);
    setForm({
      title: a.title,
      content: a.content,
      attachments: a.attachments ?? [],
      is_pinned: a.is_pinned ?? false,
      is_published: a.is_published ?? true,
    });
    setUploaded([]);
    setEditorOpen(true);
  };

  const handleTogglePin = async (a: CourseAnnouncement) => {
    setTogglingId(a.id);
    try {
      await instructorCourseAnnouncementsService.setPinned(course.id, a.id, !(a.is_pinned ?? false));
      await reload();
    } finally {
      setTogglingId(null);
    }
  };

  const handleTogglePublish = async (a: CourseAnnouncement) => {
    setTogglingId(a.id);
    try {
      await instructorCourseAnnouncementsService.setPublished(course.id, a.id, !(a.is_published ?? true));
      await reload();
    } finally {
      setTogglingId(null);
    }
  };

  const attachmentsFromUploads = React.useMemo<AnnouncementAttachment[]>(() => {
    const done = uploaded.filter((u) => u.status === "done" && u.key);
    return done.map((u) => {
      const label = (u.displayName?.trim() || u.file.name).trim();
      return {
        key: u.key!,
        url: u.url ?? null,
        name: label || u.file.name,
        content_type: u.file.type || null,
        size: u.file.size,
      };
    });
  }, [uploaded]);

  const handleSave = async () => {
    if (!form.title.trim() || !form.content.trim()) return;
    setSaving(true);
    try {
      const normalizedExisting = (form.attachments ?? []).map((a) => {
        const keyName = attachmentNameFallback(a);
        const { base: kStem, ext: kExt } = splitFilename(keyName);
        const stem = splitFilename(a.name?.trim() || keyName).base.trim() || kStem;
        return { ...a, name: stem + kExt };
      });
      const attachments = [...normalizedExisting, ...attachmentsFromUploads];
      if (editing) {
        await instructorCourseAnnouncementsService.update(course.id, editing.id, {
          title: form.title.trim(),
          content: form.content.trim(),
          attachments,
          is_pinned: form.is_pinned ?? false,
          is_published: form.is_published ?? true,
        });
      } else {
        await instructorCourseAnnouncementsService.create(course.id, {
          title: form.title.trim(),
          content: form.content.trim(),
          attachments,
          is_pinned: form.is_pinned ?? false,
          is_published: form.is_published ?? true,
        });
      }
      setEditorOpen(false);
      await reload();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (a: CourseAnnouncement) => {
    await instructorCourseAnnouncementsService.remove(course.id, a.id);
    await reload();
  };

  const saveInlineRename = React.useCallback(
    async (announcement: CourseAnnouncement) => {
      if (!inlineRename || inlineRename.announcementId !== announcement.id) return;
      const target = (announcement.attachments ?? []).find((x) => x.key === inlineRename.key);
      if (!target) return;
      const keyName = attachmentNameFallback(target);
      const { base: keyStem, ext: lockedExt } = splitFilename(keyName);
      const nextBase = inlineRename.base.trim() || keyStem;
      const nextName = nextBase + lockedExt;
      setRenameSaving(true);
      try {
        const attachments = (announcement.attachments ?? []).map((x) =>
          x.key === inlineRename.key ? { ...x, name: nextName } : x
        );
        await instructorCourseAnnouncementsService.update(course.id, announcement.id, {
          title: announcement.title,
          content: announcement.content,
          attachments,
        });
        setInlineRename(null);
        await reload();
      } finally {
        setRenameSaving(false);
      }
    },
    [inlineRename, course.id, reload]
  );

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-800 dark:text-white">
            {isArabic ? "الإعلانات" : "Announcements"}
          </h2>
          <p className="text-sm text-gray-500">
            {isArabic ? "إعلانات الكورس التي يراها الطلاب." : "Announcements your students will see."}
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
        >
          <MdAdd className="h-5 w-5" />
          {isArabic ? "إعلان جديد" : "New announcement"}
        </button>
      </header>

      {loading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<MdCampaign className="h-6 w-6" />}
          title={isArabic ? "لا توجد إعلانات" : "No announcements yet"}
          description={isArabic ? "ابدأ بإضافة أول إعلان للكورس." : "Start by adding your first announcement."}
          action={
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
            >
              <MdAdd className="h-5 w-5" />
              {isArabic ? "إعلان جديد" : "New announcement"}
            </button>
          }
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((a) => (
            <li
              key={a.id}
              className="group flex gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm hover:border-brand-300 dark:border-white/10 dark:bg-navy-800"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-500 dark:bg-brand-500/10 dark:text-brand-200">
                <MdCampaign className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <p className="truncate text-sm font-bold text-gray-800 dark:text-white">{a.title}</p>
                  {a.is_pinned ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-600 dark:bg-amber-500/15 dark:text-amber-300">
                      <MdPushPin className="h-3 w-3" />
                      {isArabic ? "مثبّت" : "Pinned"}
                    </span>
                  ) : null}
                  {a.is_published === false ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-bold text-gray-500 dark:bg-white/10 dark:text-gray-300">
                      <MdVisibilityOff className="h-3 w-3" />
                      {isArabic ? "مسودة" : "Draft"}
                    </span>
                  ) : null}
                </div>
                <p className="mt-1 whitespace-pre-wrap text-sm text-gray-600 dark:text-gray-300">
                  {a.content}
                </p>
                {(a.attachments?.length ?? 0) > 0 && (
                  <div className="mt-2.5">
                    <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
                      <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                        {isArabic ? "المرفقات" : "Attachments"}
                      </span>
                      <span className="rounded-full bg-gray-100 px-1.5 py-px text-[10px] font-semibold tabular-nums text-gray-600 dark:bg-white/10 dark:text-gray-300">
                        {(a.attachments ?? []).length}
                      </span>
                    </div>
                    <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-4">
                      {(a.attachments ?? []).map((att) => {
                        const isRenaming =
                          inlineRename?.announcementId === a.id && inlineRename.key === att.key;
                        const keyName = attachmentNameFallback(att);
                        const lockedExt = splitFilename(keyName).ext;
                        const displayName = att.name ?? keyName;
                        const sizeLabel = formatAttachmentSize(att.size ?? undefined);
                        const kind = resolveAttachmentFileKind(att.content_type || "", displayName);
                        const kindLabel =
                          kind === "image"
                            ? isArabic
                              ? "صورة"
                              : "Image"
                            : kind === "video"
                              ? isArabic
                                ? "فيديو"
                                : "Video"
                              : kind === "audio"
                                ? isArabic
                                  ? "صوت"
                                  : "Audio"
                                : kind === "pdf"
                                  ? "PDF"
                                  : kind === "archive"
                                    ? isArabic
                                      ? "أرشيف"
                                      : "Archive"
                                    : isArabic
                                      ? "ملف"
                                      : "File";

                        return (
                          <li
                            key={att.key}
                            className="group/att relative overflow-visible rounded-xl border border-gray-200/80 bg-gradient-to-br from-white to-gray-50/80 p-2 shadow-sm ring-1 ring-black/[0.02] transition-all duration-200 hover:border-brand-200/90 hover:shadow dark:border-white/10 dark:from-navy-800 dark:to-navy-900/70 dark:ring-white/[0.04] dark:hover:border-brand-500/30"
                            title={att.key}
                          >
                            {isRenaming ? (
                              <div className="flex items-center gap-2">
                                <SavedAttachmentThumb att={att} size="compact" />
                                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                                  <div className="flex min-w-0 items-center gap-1">
                                    <input
                                      type="text"
                                      value={inlineRename.base}
                                      onChange={(e) =>
                                        setInlineRename((prev) =>
                                          prev && prev.announcementId === a.id && prev.key === att.key
                                            ? { ...prev, base: e.target.value }
                                            : prev
                                        )
                                      }
                                      disabled={renameSaving}
                                      className="min-w-0 flex-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-semibold text-gray-900 outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-400/20 disabled:opacity-60 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                                      autoFocus
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") void saveInlineRename(a);
                                        if (e.key === "Escape") setInlineRename(null);
                                      }}
                                    />
                                    {lockedExt ? (
                                      <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-bold text-gray-500 dark:bg-white/10 dark:text-gray-400">
                                        {lockedExt}
                                      </span>
                                    ) : null}
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <button
                                      type="button"
                                      disabled={renameSaving}
                                      onClick={() => void saveInlineRename(a)}
                                      className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                                    >
                                      <MdCheck className="h-3.5 w-3.5" />
                                      {isArabic ? "حفظ" : "Save"}
                                    </button>
                                    <button
                                      type="button"
                                      disabled={renameSaving}
                                      onClick={() => setInlineRename(null)}
                                      className="rounded-md border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-gray-600 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-900 dark:text-gray-300 dark:hover:bg-white/5"
                                    >
                                      {isArabic ? "إلغاء" : "Cancel"}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-stretch gap-2">
                                <button
                                  type="button"
                                  disabled={!att.url}
                                  className="flex min-w-0 flex-1 items-center gap-2 overflow-visible rounded-lg text-start transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                                  onClick={() => att.url && setPreviewAtt(att)}
                                >
                                  <SavedAttachmentThumb att={att} size="compact" />
                                  <div className="min-w-0 flex-1 self-center">
                                    <p className="truncate text-xs font-bold leading-tight text-gray-900 dark:text-white">
                                      {displayName}
                                    </p>
                                    <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0 text-[10px] text-gray-500 dark:text-gray-400">
                                      {sizeLabel ? (
                                        <span className="tabular-nums text-gray-400 dark:text-gray-500">
                                          {sizeLabel}
                                        </span>
                                      ) : null}
                                      <span className="rounded bg-gray-100/90 px-1 py-px font-medium text-gray-600 dark:bg-white/10 dark:text-gray-300">
                                        {kindLabel}
                                      </span>
                                    </div>
                                    <p className="mt-1 text-[10px] font-medium text-brand-600 dark:text-brand-400">
                                      {att.url
                                        ? isArabic
                                          ? "معاينة"
                                          : "Preview"
                                        : isArabic
                                          ? "لا يوجد رابط"
                                          : "No link"}
                                    </p>
                                  </div>
                                </button>
                                <div
                                  className="flex shrink-0 flex-row items-center gap-1 border-s border-gray-100 ps-1.5 dark:border-white/10"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <button
                                    type="button"
                                    className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-200/90 bg-white text-gray-500 shadow-sm transition-colors hover:border-brand-300 hover:bg-brand-50 hover:text-brand-600 dark:border-white/10 dark:bg-navy-900 dark:text-gray-400 dark:hover:border-brand-500/40 dark:hover:bg-brand-500/10 dark:hover:text-brand-300"
                                    aria-label={isArabic ? "تعديل الاسم" : "Rename file"}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const full = att.name ?? attachmentNameFallback(att);
                                      setInlineRename({
                                        announcementId: a.id,
                                        key: att.key,
                                        base: splitFilename(full).base,
                                      });
                                    }}
                                  >
                                    <FiEdit2 className="h-3.5 w-3.5" strokeWidth={2.25} />
                                  </button>
                                  {att.url ? (
                                    <a
                                      href={att.url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-200/90 bg-white text-gray-500 shadow-sm transition-colors hover:border-brand-300 hover:bg-brand-50 hover:text-brand-600 dark:border-white/10 dark:bg-navy-900 dark:text-gray-400 dark:hover:border-brand-500/40 dark:hover:text-brand-300"
                                      aria-label={isArabic ? "فتح في تبويب جديد" : "Open in new tab"}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <FiExternalLink className="h-3.5 w-3.5" strokeWidth={2.25} />
                                    </a>
                                  ) : null}
                                </div>
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
                {a.updated_at ? (
                  <p className="mt-1 text-xs text-gray-400">
                    {formatLocaleDateTime(a.updated_at, isArabic)}
                  </p>
                ) : null}
              </div>
              <div className="flex shrink-0 items-start gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                <button
                  type="button"
                  disabled={togglingId === a.id}
                  onClick={() => handleTogglePin(a)}
                  className={`rounded-lg p-2 disabled:opacity-50 ${
                    a.is_pinned
                      ? "bg-amber-100 text-amber-600 hover:bg-amber-200 dark:bg-amber-500/15 dark:text-amber-300"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-white/10 dark:text-gray-200"
                  }`}
                  aria-label={
                    a.is_pinned
                      ? isArabic
                        ? "إلغاء التثبيت"
                        : "Unpin"
                      : isArabic
                        ? "تثبيت الإعلان"
                        : "Pin announcement"
                  }
                  title={
                    a.is_pinned
                      ? isArabic
                        ? "إلغاء التثبيت"
                        : "Unpin"
                      : isArabic
                        ? "تثبيت الإعلان"
                        : "Pin announcement"
                  }
                >
                  {a.is_pinned ? <MdPushPin className="h-4 w-4" /> : <MdOutlinePushPin className="h-4 w-4" />}
                </button>
                <button
                  type="button"
                  disabled={togglingId === a.id}
                  onClick={() => handleTogglePublish(a)}
                  className={`rounded-lg p-2 disabled:opacity-50 ${
                    a.is_published === false
                      ? "bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-white/10 dark:text-gray-300"
                      : "bg-emerald-50 text-emerald-600 hover:bg-emerald-100 dark:bg-emerald-500/15 dark:text-emerald-300"
                  }`}
                  aria-label={
                    a.is_published === false
                      ? isArabic
                        ? "نشر الإعلان"
                        : "Publish"
                      : isArabic
                        ? "إلغاء النشر"
                        : "Unpublish"
                  }
                  title={
                    a.is_published === false
                      ? isArabic
                        ? "نشر الإعلان"
                        : "Publish"
                      : isArabic
                        ? "إلغاء النشر"
                        : "Unpublish"
                  }
                >
                  {a.is_published === false ? (
                    <MdVisibilityOff className="h-4 w-4" />
                  ) : (
                    <MdVisibility className="h-4 w-4" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => openEdit(a)}
                  className="rounded-lg bg-gray-100 p-2 text-gray-600 hover:bg-gray-200 dark:bg-white/10 dark:text-gray-200"
                  aria-label={isArabic ? "تعديل" : "Edit"}
                >
                  <MdEdit className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteTarget(a)}
                  className="rounded-lg bg-red-50 p-2 text-red-500 hover:bg-red-100 dark:bg-red-500/10 dark:text-red-300"
                  aria-label={isArabic ? "حذف" : "Delete"}
                >
                  <MdDelete className="h-4 w-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <SlideOver
        open={editorOpen}
        onClose={() => !saving && setEditorOpen(false)}
        title={editing ? (isArabic ? "تعديل إعلان" : "Edit announcement") : isArabic ? "إعلان جديد" : "New announcement"}
        footer={
          <>
            <button
              type="button"
              onClick={() => !saving && setEditorOpen(false)}
              className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 dark:bg-white/10 dark:text-white"
            >
              {isArabic ? "إلغاء" : "Cancel"}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !form.title.trim() || !form.content.trim()}
              className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
            >
              {saving ? (isArabic ? "..." : "...") : isArabic ? "حفظ" : "Save"}
            </button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
            {isArabic ? "العنوان" : "Title"}
            <input
              value={form.title}
              onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
              className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
              placeholder={isArabic ? "عنوان الإعلان" : "Announcement title"}
            />
          </label>
          <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
            {isArabic ? "المحتوى" : "Content"}
            <textarea
              value={form.content}
              onChange={(e) => setForm((p) => ({ ...p, content: e.target.value }))}
              rows={10}
              className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
              placeholder={isArabic ? "اكتب محتوى الإعلان هنا..." : "Write your announcement content here..."}
            />
          </label>

          <div className="flex flex-col gap-2 rounded-xl border border-gray-200 bg-gray-50/60 p-3 dark:border-white/10 dark:bg-white/[0.03]">
            <label className="flex cursor-pointer items-center justify-between gap-3">
              <span className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                <MdPushPin className="h-4 w-4 text-amber-500" />
                {isArabic ? "تثبيت الإعلان" : "Pin announcement"}
              </span>
              <input
                type="checkbox"
                checked={form.is_pinned ?? false}
                onChange={(e) => setForm((p) => ({ ...p, is_pinned: e.target.checked }))}
                className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-400"
              />
            </label>
            <label className="flex cursor-pointer items-center justify-between gap-3">
              <span className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                <MdVisibility className="h-4 w-4 text-emerald-500" />
                {isArabic ? "نشر الإعلان" : "Publish announcement"}
              </span>
              <input
                type="checkbox"
                checked={form.is_published ?? true}
                onChange={(e) => setForm((p) => ({ ...p, is_published: e.target.checked }))}
                className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-400"
              />
            </label>
            <p className="text-[11px] text-gray-400">
              {isArabic
                ? "عند النشر، سيتم إرسال إشعار للطلاب المسجلين في الكورس."
                : "When published, enrolled students receive a push notification."}
            </p>
          </div>

          <div className="pt-2">
            <div className="mb-2 text-xs font-semibold text-gray-600 dark:text-gray-300">
              {isArabic ? "مرفقات" : "Attachments"}
            </div>

            {(form.attachments?.length ?? 0) > 0 && (
              <ul className="mb-2 flex flex-col gap-2">
                {(form.attachments ?? []).map((att) => {
                  const keyName = attachmentNameFallback(att);
                  const lockedExt = splitFilename(keyName).ext;
                  const displayBase = splitFilename(att.name ?? keyName).base;
                  const sizeLabel = formatAttachmentSize(att.size ?? undefined);
                  return (
                    <li
                      key={att.key}
                      className="flex items-center gap-2 overflow-visible rounded-xl border border-gray-200/80 bg-gradient-to-br from-white to-gray-50/80 p-2 shadow-sm dark:border-white/10 dark:from-navy-800 dark:to-navy-900/60"
                    >
                      <SavedAttachmentThumb att={att} size="compact" />
                      <div className="min-w-0 flex-1">
                        <div className="flex w-full flex-wrap items-center gap-1">
                          <input
                            type="text"
                            value={displayBase}
                            onChange={(e) =>
                              setForm((p) => ({
                                ...p,
                                attachments: (p.attachments ?? []).map((x) =>
                                  x.key === att.key ? { ...x, name: e.target.value + lockedExt } : x
                                ),
                              }))
                            }
                            disabled={saving}
                            className="min-w-0 flex-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-semibold text-gray-900 outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-400/20 disabled:opacity-60 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                            placeholder={isArabic ? "اسم الملف" : "File name"}
                            aria-label={isArabic ? "اسم المرفق" : "Attachment name"}
                          />
                          {lockedExt ? (
                            <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-bold text-gray-600 dark:bg-white/10 dark:text-gray-400">
                              {lockedExt}
                            </span>
                          ) : null}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          <p className="min-w-0 flex-1 truncate text-[11px] text-gray-400" title={att.key}>
                            {att.key}
                          </p>
                          {sizeLabel ? (
                            <span className="shrink-0 text-[11px] tabular-nums text-gray-400">
                              {sizeLabel}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() =>
                          setForm((p) => ({
                            ...p,
                            attachments: (p.attachments ?? []).filter((x) => x.key !== att.key),
                          }))
                        }
                        className="shrink-0 rounded-lg bg-red-50 px-2.5 py-1.5 text-[11px] font-semibold text-red-600 transition-colors hover:bg-red-100 disabled:opacity-60 dark:bg-red-500/15 dark:text-red-300 dark:hover:bg-red-500/25"
                      >
                        {isArabic ? "إزالة" : "Remove"}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            <FileUploader
              group="misc"
              multiple
              onChange={setUploaded}
              hint={isArabic ? "ارفع ملفات/صور وسيتم إضافتها كمرفقات للإعلان." : "Upload files/images to attach to this announcement."}
            />
          </div>
          {saving ? <SkeletonLines count={2} /> : null}
        </div>
      </SlideOver>

      <Modal
        open={!!previewAtt}
        onClose={() => setPreviewAtt(null)}
        title={previewAtt ? previewAtt.name ?? attachmentNameFallback(previewAtt) : undefined}
        size="xl"
        footer={
          previewAtt?.url ? (
            <a
              href={previewAtt.url}
              target="_blank"
              rel="noreferrer"
              className="text-sm font-semibold text-brand-600 hover:underline dark:text-brand-400"
            >
              {isArabic ? "فتح في تبويب جديد" : "Open in new tab"}
            </a>
          ) : null
        }
      >
        {previewAtt ? <AttachmentPreviewBody att={previewAtt} isArabic={isArabic} /> : null}
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && handleDelete(deleteTarget)}
        title={isArabic ? "حذف إعلان" : "Delete announcement"}
        description={
          deleteTarget
            ? isArabic
              ? `هل تريد حذف "${deleteTarget.title}"؟ لا يمكن التراجع عن هذا الإجراء.`
              : `Delete "${deleteTarget.title}"? This cannot be undone.`
            : null
        }
        confirmLabel={isArabic ? "احذف" : "Delete"}
      />
    </div>
  );
}
